import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "../supabaseClient";

export const useExpenseData = () => {
  const [user, setUser] = useState(null);
  const [balance, setBalance] = useState(0);
  const [ledger, setLedger] = useState([]);
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Helper flag to easily check admin access levels
  const isSuperAdmin = user?.role === "super_admin" || user?.is_super_admin === true;

  // =========================
  // GET CURRENT AUTH USER + PROFILE
  // =========================
  useEffect(() => {
    const getCurrentUser = async () => {
      const {
        data: { user: authUser },
        error: authError
      } = await supabase.auth.getUser();

      if (authError || !authUser) {
        console.error("Auth Error:", authError?.message);
        return;
      }

      // 🟢 Fix: Added "role" into the select statement to fetch the administrative status
      const { data: profileRow, error: profileError } = await supabase
        .from("users")
        .select("id, auth_id, name, role") 
        .eq("auth_id", authUser.id)
        .maybeSingle();

      if (profileError) {
        console.error("Profile Fetch Error:", profileError.message);
      }

      setUser({
        ...authUser,
        id: authUser.id, 
        auth_id: authUser.id,                  // Explicit Auth System UUID
        profile_db_id: profileRow?.id || null, // Explicit Internal Profile Row Table ID
        name: profileRow?.name || "System User",
        role: profileRow?.role || "user"       // 🟢 Saved role to user state context
      });
    };

    getCurrentUser();
  }, []);

  // =========================
  // FETCH USERS
  // =========================
  const fetchUsers = useCallback(async () => {
    const { data, error } = await supabase.from("users").select("*");
    if (error) {
      console.error("Users Fetch Error:", error.message);
      return;
    }
    setUsers(data || []);
  }, []);

  // Helper to extract clean arrays of IDs matching the active session actor
  const getPossibleIds = useCallback(() => {
    if (!user) return [];
    return [user.id, user.auth_id, user.profile_db_id].filter(Boolean);
  }, [user]);

  // =========================
  // FETCH TRANSACTIONS
  // =========================
  const fetchTransactions = useCallback(async () => {
    if (!user) return;
    const ids = getPossibleIds();

    let query = supabase.from("transactions").select("*");

    // 🟢 Fix: If NOT a super admin, filter requests down to the user's explicit IDs
    if (!isSuperAdmin) {
      if (ids.length === 0) return;
      query = query.in("to_user_id", ids);
    }

    const { data, error } = await query
      .eq("status", "pending")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Transaction Fetch Error:", error.message);
      return;
    }
    setRequests(data || []);
  }, [getPossibleIds, isSuperAdmin, user]);

  // =========================
  // FETCH LEDGER
  // =========================
  const fetchLedger = useCallback(async () => {
    if (!user) return;
    const ids = getPossibleIds();

    let query = supabase.from("wallet_ledger").select("*");

    // 🟢 Fix: If NOT a super admin, filter ledger rows down to user ownership rows
    if (!isSuperAdmin) {
      if (ids.length === 0) return;
      query = query.in("user_id", ids);
    }

    const { data, error } = await query.order("created_at", { ascending: false });

    if (error) {
      console.error("Ledger Fetch Error:", error.message);
      return;
    }
    setLedger(data || []);
  }, [getPossibleIds, isSuperAdmin, user]);

  // =========================
  // FETCH BALANCE
  // =========================
  const fetchBalance = useCallback(async () => {
    if (!user) return;
    const ids = getPossibleIds();

    let query = supabase.from("v_wallet_balance").select("*");

    // 🟢 Note: Keep balance filtering matching context (or view full systemic balances)
    if (!isSuperAdmin) {
      if (ids.length === 0) return;
      query = query.in("user_id", ids);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Balance Fetch Error:", error.message);
      return;
    }

    let totalBalance = 0;
    if (data && data.length > 0) {
      totalBalance = data.reduce((sum, row) => sum + Number(row.balance || 0), 0);
    }
    setBalance(totalBalance);
  }, [getPossibleIds, isSuperAdmin, user]);

  // =========================
  // REFRESH ALL
  // =========================
  const refreshAll = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    await Promise.all([fetchTransactions(), fetchLedger(), fetchBalance()]);
    setLoading(false);
  }, [user, fetchTransactions, fetchLedger, fetchBalance]);

  // INITIAL LOAD & POLLING INTERVAL
  useEffect(() => {
    if (!user) return;

    fetchUsers();
    refreshAll();

    const interval = setInterval(() => {
      refreshAll();
    }, 5000);

    return () => clearInterval(interval);
  }, [user, fetchUsers, refreshAll]);

  // =========================
  // DICTIONARY USER MAP
  // =========================
  const userMap = useMemo(() => {
    return users.reduce((acc, u) => {
      const name = u.name || "Unknown User";
      if (u.id) acc[String(u.id)] = name;
      if (u.auth_id) acc[String(u.auth_id)] = name;
      return acc;
    }, {});
  }, [users]);

  return {
    user,
    currentAuthId: user?.auth_id || null,          // Use for system session validation
    currentProfileId: user?.profile_db_id || null, // Use for operational ledger records matching
    balance,
    ledger,
    requests,
    users,
    loading,
    setLoading,
    refreshAll,
    fetchTransactions,
    pendingIncomingRequests: requests || [],
    userMap
  };
};